// src/shared/json-table/JSONTable.tsx
import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import type { ColumnDef, Filter, JSONTableProps, TableState } from "./types";
import { getDefaultState, useTablesStore } from "./store";
import { useUrlSync } from "./url-sync";
import { usePostgrestList } from "./hooks";
import { clamp, defaultOpForType } from "./utils";
import { useQueryClient } from "@tanstack/react-query";
import { buildPostgrestQuery, fetchPostgrestList } from "./postgrest";

// shadcn/ui components — adjust paths to your setup
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuCheckboxItem,
  DropdownMenuLabel, DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronDown, ChevronLeft, ChevronRight, Filter as FilterIcon, X, Columns, ArrowUpDown } from "lucide-react";

type Any = Record<string, any>;

export function JSONTable<T extends Any>(props: JSONTableProps<T>) {
  const {
    id, baseUrl, resource, columns,
    select, headers, staticFilters,
    pageSizeOptions = [10, 20, 50, 100],
    initialState, globalSearch, rowIdKey = "id" as any,
    onRowClick, queryOptions, debug
  } = props;

  // 1) Init store slot for this table id
  const init = useMemo(() => getDefaultState(initialState), [initialState]);
  const state = useTablesStore((s) => s.tables[id]);
  const initTable = useTablesStore((s) => s.initTable);
  const updateTable = useTablesStore((s) => s.updateTable);

  useEffect(() => { initTable(id, init); }, [id, init, initTable]);
  const setState = (next: TableState) => updateTable(id, (d) => Object.assign(d, next));

  // 2) URL <-> store sync
  useUrlSync(id, state ?? init, setState);

  // 3) Debounced global search
  const [search, setSearch] = useState(state?.q ?? "");
  useEffect(() => setSearch(state?.q ?? ""), [state?.q]);
  useEffect(() => {
    if (!globalSearch) return;
    const t = setTimeout(() => {
      updateTable(id, (d) => { d.q = search; d.pageIndex = 0; });
    }, globalSearch.debounceMs ?? 400);
    return () => clearTimeout(t);
  }, [id, search, globalSearch, updateTable]);

  // 4) Fetch data via React Query
  const queryState = state ?? init;
  const { data, isLoading, isError, error, url, refetch, isFetching } =
    usePostgrestList<T>({
      baseUrl, resource, select, staticFilters, headers,
      globalSearch, state: queryState, queryOptions
    });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;

  // Prefetch next page to reduce pagination flicker
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!data) return;
    const pageCountLocal = Math.max(1, Math.ceil(total / queryState.pageSize));
    const nextIndex = queryState.pageIndex + 1;
    if (nextIndex >= pageCountLocal) return;
    const nextState = { ...queryState, pageIndex: nextIndex };
    const nextUrl = buildPostgrestQuery({
      baseUrl,
      resource,
      select: select ?? "*",
      state: nextState,
      staticFilters,
      globalSearchCols: globalSearch?.columns,
    });
    queryClient.prefetchQuery({
      queryKey: ["pg", baseUrl, resource, select ?? "*", nextState],
      queryFn: () => fetchPostgrestList<T>({ url: nextUrl, headers }),
      // adopt same caching hints if provided
      ...(queryOptions as any),
    } as any);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseUrl, resource, select, JSON.stringify(queryState), total]);

  // 5) Derived
  const pageCount = Math.max(1, Math.ceil(total / queryState.pageSize));
  const from = total === 0 ? 0 : (queryState.pageIndex * queryState.pageSize + 1);
  const to = Math.min(total, (queryState.pageIndex + 1) * queryState.pageSize);

  // Helpers
  const toggleSort = (colId: string) => {
    updateTable(id, (d) => {
      const curr = d.sort.find((s) => s.id === colId);
      // cycle: none -> asc -> desc -> none
      if (!curr) {
        d.sort = [{ id: colId, desc: false }];
      } else if (curr && !curr.desc) {
        d.sort = d.sort.map((s) => s.id === colId ? { ...s, desc: true } : s);
      } else {
        d.sort = d.sort.filter((s) => s.id !== colId);
      }
      d.pageIndex = 0;
    });
  };

  const changePage = (next: number) =>
    updateTable(id, (d) => { d.pageIndex = clamp(next, 0, pageCount - 1); });

  const changeSize = (size: number) =>
    updateTable(id, (d) => { d.pageSize = size; d.pageIndex = 0; });

  const setColumnVisible = (cid: string, visible: boolean) =>
    updateTable(id, (d) => { d.columnVisibility[cid] = visible; });

  const visibleCols = columns.filter(
    (c) => queryState.columnVisibility[c.id] !== false
  );

  if (debug) {
    // eslint-disable-next-line no-console
    console.debug(`[JSONTable:${id}] state`, queryState, `url: ${url}`);
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Toolbar */}
      <div className="flex flex-col md:flex-row md:items-center gap-2">
        {/* Global search */}
        <div className="flex-1">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={globalSearch?.placeholder ?? "Search..."}
          />
        </div>

        {/* Add filter */}
        <AddFilter
          columns={columns}
          onAdd={(f) => updateTable(id, (d) => { d.filters.push(f); d.pageIndex = 0; })}
        />

        {/* Column visibility */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="gap-2">
              <Columns className="h-4 w-4" /> Columns
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {columns.map((c) => (
              <DropdownMenuCheckboxItem
                key={c.id}
                checked={queryState.columnVisibility[c.id] !== false}
                onCheckedChange={(v) => setColumnVisible(c.id, Boolean(v))}
              >
                {typeof c.header === "string" ? c.header : c.id}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Refresh */}
        <Button variant="outline" onClick={() => refetch()} className="gap-2">
          Refresh
        </Button>
      </div>

      {/* Active filters chips */}
      {queryState.filters.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {queryState.filters.map((f, i) => (
            <Badge key={`${f.id}-${i}`} variant="secondary" className="gap-1">
              <span className="font-mono">{f.id}</span>
              <span className="opacity-70">{f.op}</span>
              <span className="font-mono">
                {Array.isArray(f.value) ? `(${f.value.join(",")})` : String(f.value)}
              </span>
              <button
                className="ml-1 opacity-70 hover:opacity-100"
                onClick={() =>
                  updateTable(id, (d) => { d.filters.splice(i, 1); d.pageIndex = 0; })
                }
                aria-label="Remove filter"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => updateTable(id, (d) => { d.filters = []; d.pageIndex = 0; })}
          >
            Clear filters
          </Button>
        </div>
      )}

      {/* Table */}
      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {visibleCols.map((c) => {
                const sortable = c.enableSort;
                const active = queryState.sort.find((s) => s.id === c.id);
                return (
                  <TableHead
                    key={c.id}
                    style={c.size ? { width: c.size } : undefined}
                  >
                    <button
                      className={`inline-flex items-center gap-1 ${sortable ? "hover:underline" : "cursor-default"}`}
                      onClick={() => sortable && toggleSort(c.id)}
                      disabled={!sortable}
                    >
                      {c.header}
                      {sortable && (
                        <ArrowUpDown
                          className={`h-3.5 w-3.5 ${active ? "opacity-100" : "opacity-40"}`}
                        />
                      )}
                    </button>
                  </TableHead>
                );
              })}
            </TableRow>
          </TableHeader>

          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={`s-${i}`}>
                  {visibleCols.map((c) => (
                    <TableCell key={`${c.id}-${i}`}>
                      <Skeleton className="h-5 w-[70%]" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : isError ? (
              <TableRow>
                <TableCell colSpan={visibleCols.length}>
                  <div className="text-red-600">Error: {(error as Error)?.message}</div>
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={visibleCols.length}>
                  <div className="py-8 text-center text-muted-foreground">No results</div>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, rIdx) => {
                const key =
                  (rowIdKey && (row as any)[rowIdKey!]) ??
                  (row as any)["id"] ??
                  `${rIdx}`;
                return (
                  <TableRow
                    key={key}
                    className={onRowClick ? "cursor-pointer" : undefined}
                    onClick={() => onRowClick?.(row)}
                  >
                    {visibleCols.map((c) => {
                      const content =
                        c.cell
                          ? c.cell(row)
                          : c.accessorKey
                            ? String((row as any)[c.accessorKey])
                            : (row as any)[c.id] ?? "";
                      return (
                        <TableCell key={`${key}-${c.id}`}>
                          {content as React.ReactNode}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {total === 0 ? "0 results" : `${from}–${to} of ${total}`}
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={String(queryState.pageSize)}
            onValueChange={(v) => changeSize(Number(v))}
          >
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pageSizeOptions.map((n) => (
                <SelectItem key={n} value={String(n)}>
                  Show {n}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            variant="outline"
            size="icon"
            onClick={() => changePage(queryState.pageIndex - 1)}
            disabled={queryState.pageIndex <= 0}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          <div className="min-w-[80px] text-center text-sm">
            Page {queryState.pageIndex + 1} / {pageCount}
          </div>

          <Button
            variant="outline"
            size="icon"
            onClick={() => changePage(queryState.pageIndex + 1)}
            disabled={queryState.pageIndex + 1 >= pageCount}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

/** ————— AddFilter subcomponent ————— */

function AddFilter<T extends Any>(props: {
  columns: ColumnDef<T>[];
  onAdd: (f: Filter) => void;
}) {
  const [open, setOpen] = useState(false);
  const [col, setCol] = useState<string>("");
  const [op, setOp] = useState<string>(""); // FilterOp as string
  const [val, setVal] = useState<string>("");

  // derive defaults when column changes
  useEffect(() => {
    const c = props.columns.find((x) => x.id === col);
    const d = c?.filter?.op ?? defaultOpForType(c?.filter?.type);
    setOp(d);
    setVal("");
  }, [col, props.columns]);

  const chosen = props.columns.find((x) => x.id === col);
  const type = chosen?.filter?.type ?? "text";
  const options = chosen?.filter?.options;

  const canSubmit = col && op && (type === "boolean" || type === "select" ? val !== "" : true);

  const apply = () => {
    if (!canSubmit) return;
    const value: any =
      type === "boolean" ? (val === "true") :
      type === "select" ? val :
      val;
    props.onAdd({ id: col, op: op as any, value });
    setOpen(false);
    setCol(""); setOp(""); setVal("");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="gap-2">
          <FilterIcon className="h-4 w-4" /> Add filter
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80">
        <div className="flex flex-col gap-3">
          <div className="text-sm font-medium">Add a filter</div>

          {/* Column */}
          <div className="flex items-center gap-2">
            <div className="w-24 text-sm text-muted-foreground">Column</div>
            <Select value={col} onValueChange={setCol}>
              <SelectTrigger><SelectValue placeholder="Select column" /></SelectTrigger>
              <SelectContent>
                {props.columns.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {typeof c.header === "string" ? c.header : c.id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Operator */}
          <div className="flex items-center gap-2">
            <div className="w-24 text-sm text-muted-foreground">Operator</div>
            <Select value={op} onValueChange={setOp} disabled={!col}>
              <SelectTrigger><SelectValue placeholder="Select op" /></SelectTrigger>
              <SelectContent>
                {["eq", "neq", "gt", "gte", "lt", "lte", "ilike", "like", "in", "is"].map((o) => (
                  <SelectItem key={o} value={o}>{o}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Value */}
          <div className="flex items-center gap-2">
            <div className="w-24 text-sm text-muted-foreground">Value</div>
            {(() => {
              if (!col) return <Input disabled placeholder="Select column first" />;
              if (type === "boolean") {
                return (
                  <Select value={val} onValueChange={setVal}>
                    <SelectTrigger><SelectValue placeholder="true/false" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="true">true</SelectItem>
                      <SelectItem value="false">false</SelectItem>
                    </SelectContent>
                  </Select>
                );
              }
              if (type === "select" && options?.length) {
                return (
                  <Select value={val} onValueChange={setVal}>
                    <SelectTrigger><SelectValue placeholder="Pick" /></SelectTrigger>
                    <SelectContent>
                      {options.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                );
              }
              return (
                <Input
                  value={val}
                  onChange={(e) => setVal(e.target.value)}
                  placeholder={chosen?.filter?.placeholder ?? "value"}
                />
              );
            })()}
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={apply} disabled={!canSubmit}>Apply</Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
